import { ArchiveRounded, SyncRounded, UnarchiveRounded } from '@mui/icons-material';
import { useContext, useState } from 'react';
import { member as memberApi } from '../../../api';
import { DEFAULT_PAGINATION_FILTER } from '../../../constants';
import { MemberDetailContext } from '../../../contexts';
import { useApi, useInternationalization, useSettings } from '../../../hooks';
import { MemberContactEntity } from '../../../models';
import { ContactType, MemberContactFilter, PermissionKey } from '../../../types';
import { ArchiveMenuItem, MailSyncMenuItem } from '../../Menu';
import { MemberContactsList } from './MemberContactsList';

export const MemberContactsListAdmin = () => {
  const { settings } = useSettings();
  const { t, getTranslation } = useInternationalization();
  const { member, memberId } = useContext(MemberDetailContext);
  const [filter, setFilter] = useState<MemberContactFilter>({ isArchived: false, ...DEFAULT_PAGINATION_FILTER });

  const getAllContactsApi = useApi(memberApi.getAllContacts, { skipFetch: !member }, member?.id, filter);
  const { call: create } = useApi(memberApi.createContact, { successKey: 'common:success.save' });
  const { call: update } = useApi(memberApi.updateContact, { successKey: 'common:success.save' });
  const archiveApi = useApi(memberApi.archiveContact, { successKey: 'common:success.action' });
  const mailSyncApi = useApi(memberApi.mailSyncContacts, {
    successKey: 'common:success.action',
    errorKey: 'common:error.mailSync',
  });

  const bulkActions = [
    {
      icon: <SyncRounded />,
      label: t('common:action.mailSync'),
      onConfirm: async (items: MemberContactEntity[]) => onMailSyncConfirm(items),
      permissions: { keys: PermissionKey.MemberApproveContacts },
      disabled: !settings?.isMailSyncEnabled,
    },
    {
      icon: <ArchiveRounded />,
      label: t('common:action.archive'),
      onConfirm: async (items: MemberContactEntity[]) => onArchiveConfirm(true, items),
    },
    {
      icon: <UnarchiveRounded />,
      label: t('common:action.unarchive'),
      onConfirm: async (items: MemberContactEntity[]) => onArchiveConfirm(false, items),
    },
  ];

  const onSave = async (entity: MemberContactEntity) => {
    return entity.id ? await update(memberId, entity) : await create(memberId, entity);
  };

  const onArchiveConfirm = async (isArchived: boolean, contacts: MemberContactEntity[]) => {
    await archiveApi.call(
      memberId,
      contacts.map((i) => i.id),
      isArchived,
    );
    getAllContactsApi.refresh();
  };

  const onMailSyncConfirm = async (contacts: MemberContactEntity[]) => {
    await mailSyncApi.call(
      memberId,
      contacts.map((i) => i.id),
    );
    getAllContactsApi.refresh();
  };

  const renderActionsMenuItems = (entity: MemberContactEntity, onClick: () => void) => [
    <MailSyncMenuItem
      key="mailSync"
      permissions={{ keys: PermissionKey.MemberApproveContacts }}
      entity={entity}
      onClick={onClick}
      onMailSync={(entity) => onMailSyncConfirm([entity])}
      isVisible={
        (!!entity.mailSyncError || (!entity.mailSyncDate && !entity.isArchived)) && entity.type == ContactType.Person
      }
      shouldDelete={entity.isArchived || !!member?.isArchived}
    />,
    <ArchiveMenuItem
      key="archive"
      entity={entity}
      name={
        entity.type === ContactType.Department
          ? getTranslation(entity, 'department')
          : `${entity.firstName} ${entity.lastName}`
      }
      onClick={onClick}
      onArchiveConfirm={(value, entity) => onArchiveConfirm(value, [entity])}
      actionSuffix={t('member:contact.actions.suffix')}
    />,
  ];

  return (
    <MemberContactsList
      showSearch
      addButtonProps={{
        disabled: !member || member.isArchived,
      }}
      filter={filter}
      setFilter={setFilter}
      fetchApi={getAllContactsApi}
      onSave={onSave}
      tableProps={{
        actionMenuItems: renderActionsMenuItems,
        bulkActions: member && !member.isArchived ? bulkActions : undefined,
        bulkActionSuffix: t('member:contact.actions.suffix'),
        bulkPermissions: { keys: PermissionKey.MemberEditContacts },
        renderBulkSelection: (items) =>
          items.length === 1
            ? items[0].type === ContactType.Department
              ? getTranslation(items[0], 'department')
              : `${items[0].firstName} ${items[0].lastName}`
            : t(`member:contact.title`),
      }}
    />
  );
};
